// PointCheck.jsx
import React, { useState, useEffect } from 'react';
import './PointCheck.css';
import { useNavigate, useLocation } from 'react-router-dom';
import en from '../locales/eng.json';
import th from '../locales/th.json';
import CONFIG from '../config';

function PointCheck() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pull query params
  const queryParams = new URLSearchParams(location.search);
  const couponId = queryParams.get("coupon_id");
  const userCouponId = queryParams.get("user_coupon_id");

  // Localized text
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'TH';
  });
  const [t, setT] = useState(language === 'EN' ? en : th);

  // Admin login states
  const [showLogin, setShowLogin] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Coupon and countdown states
  const [coupons, setCoupons] = useState([]);
  const [expireTime, setExpireTime] = useState(null);     // absolute timestamp when it expires
  const [timeLeft, setTimeLeft] = useState(null);         // seconds remaining
  const [adminConfirmed, setAdminConfirmed] = useState(false);

  // Loading spinner
  const [isLoading, setIsLoading] = useState(false);

  // On mount, check if admin is already logged in
  useEffect(() => {
    const adminData = localStorage.getItem('adminData');
    if (!adminData) {
      setShowLogin(true);
    }
  }, []);

  // Update language/localization when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
    setT(language === 'EN' ? en : th);
  }, [language]);

  // ---- 1. Fetch coupon info (including `used_at` & `admin_confirm`), compute expireTime, set adminConfirmed ----
  useEffect(() => {
    if (!userCouponId) {
      return;
    }

    const fetchCouponData = async () => {
      try {
        const res = await fetch(
          `${CONFIG.API_BASE_URL}/coupon/user_coupon_by_only_id?id=${userCouponId}`
        );
        const result = await res.json();
        const couponData = result?.content?.data?.[0];

        if (couponData) {
          // 1a. Check admin_confirm flag from server
          setAdminConfirmed(Boolean(couponData.admin_confirm));

          // 1b. If not already admin_confirmed, compute expireTime from used_at
          if (!couponData.admin_confirm && couponData.used_at) {
            const baseUtc = new Date(couponData.used_at).getTime();
            const expiresAt = baseUtc + 15 * 60 * 1000; // +15 minutes in ms
            setExpireTime(expiresAt);
          }
        }
      } catch (err) {
        console.error("Error fetching coupon data:", err);
      }
    };

    fetchCouponData();
  }, [userCouponId]);

  // ---- 2. Once expireTime is set, run a setInterval to update `timeLeft` every second ----
  useEffect(() => {
    if (expireTime === null) {
      return;
    }

    // Helper to get “now” in Bangkok as a timestamp (ms)
    const getNowBangkok = () => {
      const bangkokString = new Date().toLocaleString("en-US", {
        timeZone: "Asia/Bangkok",
      });
      return new Date(bangkokString).getTime();
    };

    // Compute initial timeLeft immediately
    const updateRemaining = () => {
      const nowTs = getNowBangkok();
      const diffMs = expireTime - nowTs;
      const diffSec = Math.max(0, Math.floor(diffMs / 1000));
      setTimeLeft(diffSec);
    };

    updateRemaining();

    // Then set up interval every second
    const intervalId = setInterval(() => {
      updateRemaining();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [expireTime]);

  // ---- 3. Fetch coupon details (for display) ----
  useEffect(() => {
    const fetchCoupons = async () => {
  setIsLoading(true);
  try {
    const res = await fetch(
      `${CONFIG.API_BASE_URL}/coupon/?coupon_id=${couponId}&page=1&limit=1000`
    );
    const result = await res.json();
    if (result.content && result.content.data) {
      setCoupons(result.content.data);
    }
  } catch (error) {
    console.error("Failed to fetch coupons:", error);
  }
  setIsLoading(false);
};
if (couponId) fetchCoupons();
  }, [couponId]);

  // ---- 4. Admin login handlers ----
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const res = await fetch(`${CONFIG.API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword,
        }),
      });

      if (!res.ok) {
        if (res.status === 400 || res.status === 404) {
          setLoginError('Invalid username or password');
        } else {
          setLoginError('Server error. Please try again.');
        }
        return;
      }

      const data = await res.json();
      localStorage.setItem('adminData', JSON.stringify(data));
      setShowLogin(false);
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Network error. Please try again.');
    }
  };

  // ---- 5. Logout button handler ----
  const handleLogout = () => {
    localStorage.removeItem('adminData');
    setShowLogin(true);
  };

  // ---- 6. Admin confirms coupon usage (only if not already confirmed by server) ----
  const handleAdminConfirm = async () => {
    const adminData = JSON.parse(localStorage.getItem('adminData') || '{}');
    if (!adminData?.id) {
      setShowLogin(true);
      return;
    }
    try {
      const res = await fetch(
        `${CONFIG.API_BASE_URL}/coupon/admin_used_coupon?user_coupon_id=${userCouponId}&admin_id=${adminData.id}`,
        {
          method: 'POST',
          headers: { accept: 'application/json' },
        }
      );
      if (!res.ok) {
        const errJson = await res.json();
        console.error("Admin confirm error:", errJson);
        return;
      }
      await res.json();
      setAdminConfirmed(true);
    } catch (err) {
      console.error("Network error on admin confirm:", err);
    }
  };

  // ---- 7. If still showing login modal, render that first ----
  if (showLogin) {
    return (
      <div className="login-modal-overlay">
        <div className="login-modal">
          <h2>Admin Login</h2>
          <form onSubmit={handleLoginSubmit}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
            </div>
            {loginError && <div className="login-error">{loginError}</div>}
            <button type="submit" className="login-button">
              Log In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---- 8. Main render (when admin is logged in or login not needed) ----
  // If we are still loading or if `coupons` is not yet populated, show the spinner only
  if (isLoading || coupons.length === 0) {
    return (
      <div className="full-screen-spinner">
        <div className="spinner" />
      </div>
    );
  }

  if (coupons.length === 0) {
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      {t.pointcheck.no_coupon_found /* e.g. localized “No coupon found.” */}
    </div>
  );
}


  // From here on, `coupons.length > 0` and `isLoading === false`
  return (
    <div style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div className="point-header-gradient">
        <img
          onClick={() => navigate('/admin')}
          src="/assets/logo-ujoy.png"
          alt="logo"
          className="logo-ujoy"
        />
        <div className="right-section">
          <div className="lang-selector">
            <button
              className={`lang-button ${language === 'TH' ? 'active' : ''}`}
              onClick={() => setLanguage('TH')}
            >
              TH
            </button>
            |
            <button
              className={`lang-button ${language === 'EN' ? 'active' : ''}`}
              onClick={() => setLanguage('EN')}
            >
              EN
            </button>
          </div>
          {/* Show Logout button if adminData exists */}
          {localStorage.getItem('adminData') && (
            <button
              onClick={handleLogout}
              className="logout-button"
              style={{
                marginLeft: '1rem',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Logout
            </button>
          )}
        </div>
      </div>

      <div className="point-header-bar">
        <span className="point-header-title">{t.pointcheck.pointcheck}</span>
      </div>

      {/* Coupon Section */}
      <div className="coupon-section">
        {adminConfirmed ? (
          <div className="coupon-used">
            <h2>{t.pointcheck.coupon_used_success}</h2>
            <div className="mock-coupon">
              <h3>{coupons[0].name}</h3>
              <p className="coupon-description-preline">{coupons[0].description}</p>
               <p>{t.pointcheck.discount} <b>{coupons[0].discount_value}</b> {t.pointcheck.baht}</p>
              <b>
                {t.pointcheck.coupon_code}: UF{userCouponId}CID{couponId},{coupons[0].discount_value}
              </b>
            </div>
          </div>
        ) : timeLeft !== null && timeLeft > 0 ? (
          <div className="coupon-valid">
            <h2>{t.pointcheck.can_use_coupon}</h2>
            <p>
              {t.pointcheck.coupon_used} {Math.floor(timeLeft / 60)} นาที{' '}
              {timeLeft % 60} {t.pointcheck.sec}
            </p>
            <button
              className="admin-confirm-button"
              onClick={handleAdminConfirm}
            >
              {t.countdown.confirm_button_text || 'Confirm Used'}
            </button>
            <div className="mock-coupon">
              <h3>{coupons[0].name}</h3>
              <p className="coupon-description-preline">{coupons[0].description}</p>
               <p>{t.pointcheck.discount} <b>{coupons[0].discount_value}</b> {t.pointcheck.baht}</p>
              <b>
                {t.pointcheck.coupon_code}: UF{userCouponId}CID{couponId},{coupons[0].discount_value}
              </b>
            </div>
          </div>
        ) : (
          <div className="coupon-expired">
            <h2>{t.pointcheck.cannot_use_coupon}</h2>
            <p>{t.pointcheck.coupon_expired}</p>
            <div className="mock-coupon">
              <h3>{coupons[0].name}</h3>
              <p className="coupon-description-preline">{coupons[0].description}</p>
              <p>{t.pointcheck.discount} <b>{coupons[0].discount_value}</b> {t.pointcheck.baht}</p>
              <b>
                {t.pointcheck.coupon_code}: UF{userCouponId}CID{couponId},{coupons[0].discount_value}
              </b>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PointCheck;
